/**
 * RLQ — RoleGate
 *
 * Lightweight role gate used by the four lobby surfaces. If the active
 * user does not satisfy the gate, we redirect to `redirectTo` (default
 * `/rlq/queue`) and surface a soft toast so the user understands why
 * they bounced.
 *
 * This is RLQ-local on purpose — there is no shell-level RoleGate today
 * (per `feedback_role_gate_local.md` 2026-04-18: "build a route guard at
 * the module that needs it; resist a global until 3+ modules need the
 * same gate"). When TasksKanban + RAP get their own gates we'll lift
 * this into `components/common/RoleGate`.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Navigate } from 'react-router';

import { Toast } from '@/components/common/Toast';
import { useAuth } from '@/contexts/AuthContext';

export interface RoleGateProps {
  /** Allowed primary-role values. Empty array = no gate. */
  roles: ReadonlyArray<string>;
  /** Where to send the user when blocked. */
  redirectTo?: string;
  /** Soft-toast message on a bounce. */
  bounceMessage?: string;
  /**
   * Optional escape hatch for the kiosk surface — when true, a `?kiosk=1`
   * query param is honored as if the user had the Kiosk role. Useful for
   * dev/demo without provisioning a kiosk-bound AAD account.
   */
  allowKioskQueryParam?: boolean;
  children: ReactNode;
}

function readKioskQueryParam(): boolean {
  if (typeof window === 'undefined') return false;
  const search = window.location.search;
  return search.includes('kiosk=1') || search.includes('kiosk=true');
}

export function RoleGate({
  roles,
  redirectTo = '/rlq/queue',
  bounceMessage = "That view isn't available for your role.",
  allowKioskQueryParam = false,
  children,
}: RoleGateProps): JSX.Element {
  const { user, loading } = useAuth();

  const allowed = useMemo<boolean>(() => {
    if (loading) return true; // hold the gate until auth resolves
    if (roles.length === 0) return true;
    if (allowKioskQueryParam && readKioskQueryParam()) return true;
    if (!user?.primaryRole) return false;
    return roles.includes(user.primaryRole);
  }, [user?.primaryRole, loading, roles, allowKioskQueryParam]);

  const [showBounce, setShowBounce] = useState<boolean>(false);

  useEffect(() => {
    if (!loading && !allowed) {
      setShowBounce(true);
    }
  }, [loading, allowed]);

  if (loading) return <div className="lobby-page__bootstrap" aria-busy="true" />;

  if (!allowed) {
    return (
      <>
        {showBounce && (
          <div
            style={{
              position: 'fixed',
              bottom: 24,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1000,
            }}
          >
            <Toast
              variant="warn"
              message={bounceMessage}
              autoHideMs={3500}
              onClose={() => setShowBounce(false)}
            />
          </div>
        )}
        <Navigate to={redirectTo} replace />
      </>
    );
  }

  return <>{children}</>;
}
